<template>
    <!--
        Kept in the layout but clipped to zero height until Klarna has actually painted, so a
        placement that never arrives is never seen. It is not display:none, because the SDK
        needs a real layout box to render into.
    -->
    <div
        v-if="!hasKlarnaError && showKlarna"
        :class="
            isPlacementReady ? 'mt-5 flex h-17 items-center justify-center rounded-md lg:border lg:border-border/40' : 'h-0 overflow-hidden opacity-0'
        "
        :aria-hidden="!isPlacementReady"
    >
        <klarna-placement
            id="klarna-placement"
            class="flex size-full items-center bg-gray-100/50"
            :data-locale="locale"
            :data-purchase-amount="Math.round(price * 100)"
            data-key="credit-promotion-badge"
        />
    </div>
</template>

<script setup lang="ts">
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';

type ProductKlarnaProps = {
    price: number;
};

const props = defineProps<ProductKlarnaProps>();

const productPageStore = useProductPageStore();

const klarnaScriptUrl = 'https://js.klarna.com/web-sdk/v1/klarna.js';
// Klarna's badge normally paints well within a second; anything beyond this is a silent failure.
const placementPollIntervalMs = 250;
const placementTimeoutMs = 3000;

const locale = productPageStore.store.language_locale.replace('_', '-');
/** Only true once Klarna has actually painted its badge; nothing is shown before that. */
const isPlacementReady = ref(false);
const hasKlarnaError = ref(false);

/** Null whenever Klarna cannot possibly render, so the block is never mounted at all. */
const klarnaConfig = computed((): { clientId: string; environment: string } | null => {
    const klarna = productPageStore.klarna;

    if (!klarna?.active || !klarna.client_id || !klarna.environment) {
        return null;
    }

    if (props.price <= 0) {
        return null;
    }

    return { clientId: klarna.client_id, environment: klarna.environment };
});

const showKlarna = computed((): boolean => klarnaConfig.value !== null);

/** Klarna availability is a merchant-level setting, so one failure predicts the rest of the visit. */
const unavailableStorageKey = computed((): string | undefined =>
    klarnaConfig.value ? `klarna-unavailable:${klarnaConfig.value.clientId}:${locale}` : undefined,
);

function rememberKlarnaUnavailable(): void {
    const key = unavailableStorageKey.value;

    if (!key) {
        return;
    }

    sessionStorage.setItem(key, '1');
}

function isKlarnaKnownUnavailable(): boolean {
    const key = unavailableStorageKey.value;

    if (!key) {
        return false;
    }

    return sessionStorage.getItem(key) !== null;
}

function loadKlarnaScript(): void {
    const config = klarnaConfig.value;

    if (!config) {
        return;
    }

    const existingScript = document.querySelector(`script[src="${klarnaScriptUrl}"]`);

    if (existingScript) {
        return;
    }

    const script = document.createElement('script');
    script.src = klarnaScriptUrl;
    script.setAttribute('data-environment', config.environment);
    script.setAttribute('data-client-id', config.clientId);
    script.onerror = () => {
        hasKlarnaError.value = true;
        rememberKlarnaUnavailable();
    };
    document.head.appendChild(script);
}

onBeforeMount(() => {
    if (showKlarna.value) {
        loadKlarnaScript();
    }
});

let placementPoll: ReturnType<typeof setInterval> | undefined;

function waitForKlarnaLoad(): Promise<void> {
    const maxAttempts = Math.ceil(placementTimeoutMs / placementPollIntervalMs);
    let attempts = 0;

    return new Promise<void>(resolve => {
        placementPoll = setInterval(() => {
            attempts++;

            const host = document.querySelector('#klarna-placement');
            const span = host?.querySelector('span');

            if (span) {
                clearInterval(placementPoll);
                isPlacementReady.value = true;
                resolve();
                return;
            }

            if (attempts >= maxAttempts) {
                clearInterval(placementPoll);
                hasKlarnaError.value = true;
                rememberKlarnaUnavailable();
                resolve();
            }
        }, placementPollIntervalMs);
    });
}

// Klarna's SDK fires an unhandled rejection with "454: Handling is disabled" when
// OSM is not available for the merchant. Catch it here to hide the widget cleanly.
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const message = String(event.reason?.message ?? event.reason ?? '');
    if (!message.includes('454')) {
        return;
    }

    hasKlarnaError.value = true;
    rememberKlarnaUnavailable();
    event.preventDefault();
}

onMounted(async () => {
    if (!showKlarna.value) {
        return;
    }

    // Set after hydration, not before, so the client keeps rendering what the server sent.
    if (isKlarnaKnownUnavailable()) {
        hasKlarnaError.value = true;
        return;
    }

    // Kept listening until unmount: Klarna can still fail after it painted.
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    await waitForKlarnaLoad();
});

onUnmounted(() => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    // A visitor who leaves before the placement paints must not keep the poll running.
    clearInterval(placementPoll);
});
</script>

<style scoped>
/* Styling the Klarna placement here because it won't support tailwind for some reason (and because the docs say so) */
#klarna-placement::part(osm-container) {
    background-color: transparent;
    border: 0;
    font-family: var(--preferred-font), 'Segoe UI', serif;
}
</style>
